// components/Admin/AdminBookingList.jsx
import React, { useState } from 'react';
import './AdminBookingList.css';

const AdminBookingList = ({
  bookings = [],
  selectedBookings = [],
  toggleSelectBooking,
  canApprove = false,
  setSelectedBooking,
  setShowRejectionModal,
  setShowChangeLocationModal,
  setShowViewDetailsModal,
  setAvailableLocations,
  facilities = [],
  handleAdminAction,
  handleApproveBooking,
  handleRejectBooking,
  handleAddToWaitlist,
  handleViewDetails,
  showAllStatus = false
}) => {
  
  // Internal state for tabs and pagination
  const [activeStatusTab, setActiveStatusTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Debug: Log bookings when component receives them
  console.log('📊 AdminBookingList - Bookings received:', bookings.length);
  console.log('📄 Bookings with dependent_approval_file:', bookings.filter(b => b.dependent_approval_file).length);
  
  // Log first booking with approval file
  const bookingWithApproval = bookings.find(b => b.dependent_approval_file);
  if (bookingWithApproval) {
    console.log('🔍 Sample booking with approval file:', {
      id: bookingWithApproval.id,
      booking_number: bookingWithApproval.booking_number,
      dependent_approval_file: bookingWithApproval.dependent_approval_file,
      member_membership_type: bookingWithApproval.member_membership_type,
      member_relationship_type: bookingWithApproval.member_relationship_type
    });
  }

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'pending': return 'status-pending';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'cancelled': return 'status-cancelled';
      case 'waitlist': return 'status-waitlist';
      case 'location_changed': return 'status-location-changed';
      default: return 'status-default';
    }
  };

  const getTypeBadgeClass = (type) => {
    switch(type) {
      case 'facility': return 'type-facility';
      case 'equipment': return 'type-equipment';
      case 'section': return 'type-section';
      case 'event': return 'type-event';
      default: return 'type-default';
    }
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    if (time.includes(':')) {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
    return time;
  };

  const needsAdminAction = (booking) => {
    if (booking.status === 'approved' || booking.status === 'rejected') return false;
    if (booking.status === 'cancelled') return false;
    return true;
  };

  // Helper function to check if booking requires approval document review
  const requiresApprovalDocument = (booking) => {
    const hasApprovalFile = !!booking.dependent_approval_file;
    const isDependentNotSpouse = booking.member_membership_type === 'dependent' && 
                                  booking.member_relationship_type !== 'spouse';
    const memberRequiresApproval = booking.member_requires_approval === true;
    
    const result = hasApprovalFile || isDependentNotSpouse || memberRequiresApproval;
    
    if (result) {
      console.log('🔍 Booking requires approval doc:', {
        booking_id: booking.id,
        hasApprovalFile,
        isDependentNotSpouse,
        memberRequiresApproval,
        dependent_approval_file: booking.dependent_approval_file
      });
    }
    
    return result;
  };

  // Helper function to get approval document URL
  const getApprovalDocumentUrl = (booking) => {
    if (booking.dependent_approval_file) {
      let url = booking.dependent_approval_file;
      if (url.startsWith('/')) {
        url = `https://nlngraclub.com${url}`;
      }
      return url;
    }
    return null;
  };

  // Filter bookings by status tab
  const getFilteredBookings = () => {
    if (activeStatusTab === 'all') return bookings;
    return bookings.filter(booking => booking.status === activeStatusTab);
  };

  const filteredByStatus = getFilteredBookings();
  
  // Get status counts
  const statusCounts = {
    all: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    approved: bookings.filter(b => b.status === 'approved').length,
    rejected: bookings.filter(b => b.status === 'rejected').length,
    waitlist: bookings.filter(b => b.status === 'waitlist').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    location_changed: bookings.filter(b => b.status === 'location_changed').length
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBookings = filteredByStatus.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredByStatus.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const gridElement = document.querySelector('.bookings-grid');
    if (gridElement) {
      gridElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleTabChange = (tab) => {
    setActiveStatusTab(tab);
    setCurrentPage(1);
  };

  // Status tabs configuration
  const statusTabs = [
    { id: 'all', label: 'All', icon: 'fa-solid fa-list', color: '#64748b', count: statusCounts.all },
    { id: 'pending', label: 'Pending', icon: 'fa-solid fa-clock', color: '#f59e0b', count: statusCounts.pending },
    { id: 'approved', label: 'Approved', icon: 'fa-solid fa-check-circle', color: '#10b981', count: statusCounts.approved },
    { id: 'rejected', label: 'Rejected', icon: 'fa-solid fa-times-circle', color: '#ef4444', count: statusCounts.rejected },
    { id: 'waitlist', label: 'Waitlist', icon: 'fa-solid fa-hourglass-half', color: '#8b5cf6', count: statusCounts.waitlist },
    { id: 'cancelled', label: 'Cancelled', icon: 'fa-solid fa-ban', color: '#6b7280', count: statusCounts.cancelled }
  ];

  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null;
    
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) pages.push(i);
          pages.push('...');
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(totalPages);
        }
      }
      return pages;
    };
    
    return (
      <div className="pagination-container">
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <i className="fa-solid fa-chevron-left"></i> Previous
        </button>
        
        <div className="pagination-numbers">
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              className={`pagination-number ${page === currentPage ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
              onClick={() => page !== '...' && handlePageChange(page)}
              disabled={page === '...'}
            >
              {page}
            </button>
          ))}
        </div>
        
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next <i className="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    );
  };

  if (!bookings || bookings.length === 0) {
    return (
      <div className="no-bookings">
        <i className="fa-solid fa-calendar-xmark"></i>
        <p>No bookings found</p>
      </div>
    );
  }

  return (
    <div className="admin-booking-list">
      {/* Status Tabs */}
      <div className="status-tabs">
        {statusTabs.map(tab => (
          <button
            key={tab.id}
            className={`status-tab ${activeStatusTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
            style={{ 
              color: activeStatusTab === tab.id ? tab.color : '#64748b'
            }}
          >
            <i className={tab.icon} style={{ color: activeStatusTab === tab.id ? tab.color : '#94a3b8' }}></i>
            <span>{tab.label}</span>
            {tab.count > 0 && (
              <span className="tab-count" style={{ 
                backgroundColor: '#ffffff',
                color: tab.color,
                borderColor: '#e5e7eb'
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Bookings Grid - 3 columns */}
      {currentBookings.length > 0 ? (
        <>
          <div className="bookings-grid">
            {currentBookings.map(booking => (
              <div key={booking.id} className="booking-card">
                <div className="booking-card-header">
                  <div className="booking-select">
                    <input
                      type="checkbox"
                      checked={selectedBookings?.includes(booking.id) || false}
                      onChange={() => toggleSelectBooking(booking.id)}
                    />
                  </div>
                  <div className="booking-badges">
                    <span className={`type-badge ${getTypeBadgeClass(booking.booking_type)}`}>
                      {booking.booking_type?.toUpperCase() || 'N/A'}
                    </span>
                    <span className={`status-badge ${getStatusBadgeClass(booking.status)}`}>
                      {booking.status?.toUpperCase() || 'PENDING'}
                    </span>
                    {requiresApprovalDocument(booking) && (
                      <span className="dependent-badge" title="Dependent Member - Requires Approval Document">
                        <i className="fa-solid fa-file-upload"></i> DEPENDENT
                      </span>
                    )}
                    {booking.is_past && (
                      <span className="date-badge past">
                        <i className="fa-regular fa-calendar-xmark"></i> PAST
                      </span>
                    )}
                    {booking.is_today && (
                      <span className="date-badge today">
                        <i className="fa-regular fa-calendar-check"></i> TODAY
                      </span>
                    )}
                    {booking.is_upcoming && (
                      <span className="date-badge upcoming">
                        <i className="fa-regular fa-calendar"></i> UPCOMING
                      </span>
                    )}
                  </div>
                  <div className="booking-number">
                    <i className="fa-solid fa-hashtag"></i>
                    {booking.booking_number || booking.id}
                  </div>
                </div>

                <div className="booking-card-body">
                  {/* Member Information */}
                  <div className="info-section member-info">
                    <h4>
                      <i className="fa-solid fa-user"></i>
                      Member Details
                    </h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <label>Name:</label>
                        <span className="member-name">
                          {booking.member_full_name || 
                           `${booking.member_first_name || booking.first_name} ${booking.member_last_name || booking.last_name}`}
                        </span>
                      </div>
                      <div className="info-item">
                        <label>Membership #:</label>
                        <span>{booking.member_membership_number || booking.membership_number || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <label>Email:</label>
                        <span className="email-value">{booking.member_email || booking.email || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <label>Phone:</label>
                        <span className="phone-value">{booking.member_phone || booking.phone || 'N/A'}</span>
                      </div>
                    </div>
                    
                    {/* Member Type - Separate row with separator */}
                    <div className="member-type-row">
                      <div className="info-item full-width">
                        <label>Member Type:</label>
                        <span className={`member-type-badge ${booking.member_membership_type === 'primary' ? 'primary' : 'dependent'}`}>
                          {booking.member_membership_type === 'primary' ? 'Primary Member' : 'Dependent Member'}
                          {booking.member_relationship_type && booking.member_membership_type === 'dependent' && (
                            <span className="relationship-badge">({booking.member_relationship_type})</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Date & Time Information */}
                  <div className="info-section datetime-info">
                    <h4>
                      <i className="fa-regular fa-calendar"></i>
                      Date & Time
                    </h4>
                    <div className="datetime-grid">
                      <div className="datetime-item">
                        <i className="fa-regular fa-calendar-alt"></i>
                        <div>
                          <label>Booking Date:</label>
                          <span className="booking-date">
                            {booking.booking_date ? new Date(booking.booking_date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="datetime-item">
                        <i className="fa-regular fa-clock"></i>
                        <div>
                          <label>Time:</label>
                          <span className="booking-time">
                            {booking.start_time && booking.end_time 
                              ? `${formatTime(booking.start_time)} - ${formatTime(booking.end_time)}`
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Information */}
                  <div className="info-section location-info">
                    <h4>
                      <i className="fa-solid fa-location-dot"></i>
                      Location
                    </h4>
                    <div className="location-details">
                      <div className="location-item">
                        <label>Venue:</label>
                        <span className="location-name">{booking.location || booking.facility_name || 'N/A'}</span>
                      </div>
                      {booking.event_type && (
                        <div className="location-item">
                          <label>Event Type:</label>
                          <span className="event-type">{booking.event_type}</span>
                        </div>
                      )}
                      {booking.guests && (
                        <div className="location-item">
                          <label>Guests:</label>
                          <span>{booking.guests}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* DEPENDENT APPROVAL DOCUMENT SECTION */}
                  {requiresApprovalDocument(booking) && (
                    <div className="info-section approval-doc-info">
                      <h4>
                        <i className="fa-solid fa-file-pdf"></i>
                        Approval Document
                        <span className="required-badge">Required for Dependent</span>
                      </h4>
                      <div className="approval-doc-content">
                        {getApprovalDocumentUrl(booking) ? (
                          <div className="doc-available">
                            <div className="doc-preview">
                              <i className="fa-solid fa-file-pdf doc-icon"></i>
                              <div className="doc-details">
                                <span className="doc-filename">Approval Document</span>
                                <span className="doc-size">Click to view</span>
                              </div>
                            </div>
                            <a 
                              href={getApprovalDocumentUrl(booking)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn-view-doc"
                              onClick={() => console.log('Opening document:', getApprovalDocumentUrl(booking))}
                            >
                              <i className="fa-solid fa-eye"></i> View Document
                            </a>
                          </div>
                        ) : (
                          <div className="doc-missing">
                            <i className="fa-solid fa-exclamation-triangle"></i>
                            <span>No approval document uploaded</span>
                            {booking.status === 'pending' && (
                              <span className="warning-text"> - This booking requires admin review</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes/Special Requests */}
                  {booking.notes && (
                    <div className="info-section notes-info">
                      <h4>
                        <i className="fa-solid fa-note-sticky"></i>
                        Notes
                      </h4>
                      <div className="notes-content">
                        {booking.notes}
                      </div>
                    </div>
                  )}

                  {/* Equipment Requests */}
                  {booking.equipment_items && booking.equipment_items.length > 0 && (
                    <div className="info-section equipment-info">
                      <h4>
                        <i className="fa-solid fa-tools"></i>
                        Equipment Requested
                      </h4>
                      <div className="equipment-list">
                        {booking.equipment_items.map((item, idx) => (
                          <span key={idx} className="equipment-tag">
                            {typeof item === 'string' ? item : item.name}
                            {item.quantity && ` (${item.quantity})`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Jersey Request */}
                  {booking.jersey_request && booking.jersey_request.size && (
                    <div className="info-section jersey-info">
                      <h4>
                        <i className="fa-solid fa-shirt"></i>
                        Jersey Request
                      </h4>
                      <div className="jersey-details">
                        <span><strong>Size:</strong> {booking.jersey_request.size}</span>
                        {booking.jersey_request.quantity && (
                          <span><strong>Quantity:</strong> {booking.jersey_request.quantity}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contact Information */}
                  {(booking.contact_phone || booking.contact_whatsapp || booking.contact_email) && (
                    <div className="info-section contact-info">
                      <h4>
                        <i className="fa-solid fa-address-card"></i>
                        Contact Details
                      </h4>
                      <div className="contact-details">
                        {booking.contact_phone && (
                          <div className="contact-item">
                            <i className="fa-solid fa-phone"></i>
                            <span>{booking.contact_phone}</span>
                          </div>
                        )}
                        {booking.contact_whatsapp && (
                          <div className="contact-item">
                            <i className="fa-brands fa-whatsapp"></i>
                            <span>{booking.contact_whatsapp}</span>
                          </div>
                        )}
                        {booking.contact_email && (
                          <div className="contact-item">
                            <i className="fa-solid fa-envelope"></i>
                            <span>{booking.contact_email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rejection Reason */}
                  {booking.status === 'rejected' && booking.rejection_reason && (
                    <div className="info-section rejection-info">
                      <h4>
                        <i className="fa-solid fa-circle-exclamation"></i>
                        Rejection Reason
                      </h4>
                      <div className="rejection-content">
                        {booking.rejection_reason}
                      </div>
                    </div>
                  )}

                  {/* Location Change History */}
                  {booking.status === 'location_changed' && booking.location_change_reason && (
                    <div className="info-section change-info">
                      <h4>
                        <i className="fa-solid fa-location-dot"></i>
                        Location Change
                      </h4>
                      <div className="change-details">
                        <div className="change-item">
                          <label>Previous Location:</label>
                          <span>{booking.previous_location || 'N/A'}</span>
                        </div>
                        <div className="change-item">
                          <label>New Location:</label>
                          <span>{booking.location}</span>
                        </div>
                        <div className="change-item">
                          <label>Reason:</label>
                          <span>{booking.location_change_reason}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="booking-card-footer">
                  {canApprove && needsAdminAction(booking) && (
                    <div className="action-buttons">
                      <button 
                        className="btn-approve"
                        title="Approve"
                        onClick={() => handleApproveBooking ? handleApproveBooking(booking.id) : handleAdminAction(booking.id, 'approved')}
                      >
                        <i className="fa-solid fa-check"></i>
                        <span className="btn-text">Approve</span>
                      </button>
                      <button 
                        className="btn-reject"
                        title="Reject"
                        onClick={() => handleRejectBooking ? handleRejectBooking(booking) : (() => {
                          setSelectedBooking(booking);
                          setShowRejectionModal(true);
                        })()}
                      >
                        <i className="fa-solid fa-times"></i>
                        <span className="btn-text">Reject</span>
                      </button>
                      <button 
                        className="btn-change"
                        title="Change Location"
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowChangeLocationModal(true);
                        }}
                      >
                        <i className="fa-solid fa-location-dot"></i>
                        <span className="btn-text">Change Location</span>
                      </button>
                      <button 
                        className="btn-waitlist"
                        title="Add to Waitlist"
                        onClick={() => handleAddToWaitlist ? handleAddToWaitlist(booking.id) : handleAdminAction(booking.id, 'waitlist')}
                      >
                        <i className="fa-solid fa-clock"></i>
                        <span className="btn-text">Waitlist</span>
                      </button>
                      <button 
                        className="btn-view"
                        title="View Details"
                        onClick={() => handleViewDetails ? handleViewDetails(booking) : (() => {
                          setSelectedBooking(booking);
                          setShowViewDetailsModal(true);
                        })()}
                      >
                        <i className="fa-solid fa-eye"></i>
                        <span className="btn-text">View</span>
                      </button>
                    </div>
                  )}
                  
                  {!needsAdminAction(booking) && (
                    <div className="action-buttons single">
                      <button 
                        className="btn-view"
                        onClick={() => handleViewDetails ? handleViewDetails(booking) : (() => {
                          setSelectedBooking(booking);
                          setShowViewDetailsModal(true);
                        })()}
                      >
                        <i className="fa-solid fa-eye"></i>
                        <span className="btn-text">View Details</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination */}
          <Pagination />
          
          {filteredByStatus.length > itemsPerPage && (
            <div className="pagination-info">
              Showing {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredByStatus.length)} of {filteredByStatus.length} bookings
            </div>
          )}
        </>
      ) : (
        <div className="no-bookings">
          <i className="fa-solid fa-inbox"></i>
          <p>No {activeStatusTab !== 'all' ? activeStatusTab : ''} bookings found</p>
        </div>
      )}
    </div>
  );
};

export default AdminBookingList;